import { ContractNature, FoaPart } from "../../../../../autogen/bff-api";

export interface FoaPartBanner {
  messageKey: string;
  status: "info" | "warning";
}

// UX-hint thresholds mirroring FoaPartClassifier, used to classify coarsely before the event exists
// (the create modal) and to drive the value-only pending hint. Keep in sync with FoaPartClassifier.kt.
export const NATIONAL_THRESHOLD_NOK = 1_300_000;
export const EEA_THRESHOLD_SUPPLIES_SERVICES_NOK = 2_500_000;
export const EEA_THRESHOLD_WORKS_NOK = 62_900_000;

// Banner copy is consumed via a dynamic t(banner.messageKey) call, so langen can't see it.
// These magic comments register the keys statically — keep them verbatim with the switch below.
// t('This procurement is below the Procurement Act threshold — it should run in the simplified flow')
// t('This is a Del I procurement — it should run in the simplified flow')
// t('FOA Part II — national procurement, announced on Doffin')
// t('FOA Part III — over the EEA threshold, announced on Doffin and TED')
// t('FOA Part IV — special services')
// t('Over the national threshold — a public procurement announced on Doffin. Choose a contract type for the exact FOA part.')
export const foaPartBanner = (
  foaPart: FoaPart | null | undefined,
  estimatedValueNok?: number | null,
): FoaPartBanner | null => {
  if (foaPart != null) {
    switch (foaPart) {
      case FoaPart.OutsideFoa:
        return { messageKey: "This procurement is below the Procurement Act threshold — it should run in the simplified flow", status: "info" };
      case FoaPart.PartI:
        return { messageKey: "This is a Del I procurement — it should run in the simplified flow", status: "info" };
      case FoaPart.PartIi:
        return { messageKey: "FOA Part II — national procurement, announced on Doffin", status: "info" };
      case FoaPart.PartIii:
        return { messageKey: "FOA Part III — over the EEA threshold, announced on Doffin and TED", status: "info" };
      case FoaPart.PartIv:
        return { messageKey: "FOA Part IV — special services", status: "info" };
      default: {
        const _exhaustive: never = foaPart;
        return _exhaustive;
      }
    }
  }
  if (estimatedValueNok != null && estimatedValueNok >= NATIONAL_THRESHOLD_NOK) {
    return {
      messageKey:
        "Over the national threshold — a public procurement announced on Doffin. Choose a contract type for the exact FOA part.",
      status: "info",
    };
  }
  return null;
};

// Coarse pre-creation routing. The fork is the single national threshold (1.3M), already
// declared above for the in-wizard banner. The exact FOA part (Part II/III/IV) is NOT computed
// here — it stays server-derived once the event exists (see FoaPartClassifier).
export const routesToSimplifiedFlow = (estimatedValueNok: number | null | undefined): boolean =>
  estimatedValueNok != null && estimatedValueNok < NATIONAL_THRESHOLD_NOK;

// Coarse FOA part for the create modal: the contract-type EEA threshold decides Part II (Doffin)
// vs Part III (Doffin + TED). Works' EEA threshold dwarfs goods/services', so 2M works is Part II.
// null (no type chosen) lets foaPartBanner show the "choose a contract type" hint.
const coarsePublicFoaPart = (
  estimatedValueNok: number,
  contractNature: ContractNature | null | undefined,
): FoaPart | null => {
  if (contractNature == null) return null;
  const eeaThresholdNok =
    contractNature === ContractNature.Works
      ? EEA_THRESHOLD_WORKS_NOK
      : EEA_THRESHOLD_SUPPLIES_SERVICES_NOK;
  return estimatedValueNok >= eeaThresholdNok ? FoaPart.PartIii : FoaPart.PartIi;
};

// The modal forks on the national threshold: below it, the part-agnostic simplified-flow message
// (claiming "Del I" would be wrong for below-500K drafts, which classify as OutsideFoa); above it,
// the contract-aware FOA-part banner shared with the wizard's Info step.
// t('This procurement runs in the simplified flow')
export const modalRoutingBanner = (
  estimatedValueNok: number | null | undefined,
  contractNature?: ContractNature | null,
): FoaPartBanner | null => {
  if (estimatedValueNok == null) return null;
  if (routesToSimplifiedFlow(estimatedValueNok)) {
    return { messageKey: "This procurement runs in the simplified flow", status: "info" };
  }
  return foaPartBanner(coarsePublicFoaPart(estimatedValueNok, contractNature), estimatedValueNok);
};
